import React, { useState } from 'react';
import PropTypes from 'prop-types';
const classNames = require('classnames');

/**
 * PasswordInput Component
 * A password input with show/hide toggle functionality
 * 
 * @param {Object} props
 * @param {string} props.label - Label text for the input
 * @param {string} props.name - Input name
 * @param {Function} props.register - react-hook-form register function
 * @param {Object} props.errors - Error object from react-hook-form
 * @param {Object} props.validation - Validation rules for react-hook-form
 * @param {string} props.backgroundImage - Background image URL
 * @param {string} props.backgroundPosition - Background image position
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional classes for the input
 */
export const PasswordInput = (props) => {
    const { 
        label, 
        name, 
        register, 
        errors, 
        validation = {},
        backgroundImage,
        backgroundPosition = "4px -70px",
        placeholder = "",
        className = ""
    } = props;
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="mb-3">
            {label && <label>{label}</label>}
            <div style={{ position: 'relative' }}>
                <input 
                    style={{
                        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                        backgroundPosition: backgroundPosition
                    }} 
                    type={showPassword ? "text" : "password"} 
                    name={name}
                    placeholder={placeholder}
                    className={classNames("form-control", "auth-input", className, { "is-invalid": errors })} 
                    {...register(name, validation)} 
                />
                <span 
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    {showPassword ? (
                        // Eye with slash (hidden)
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--bs-secondary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                    ) : (
                        // Eye open (visible)
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--bs-secondary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                    )}
                </span>
            </div>
            {errors && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                    {errors.message || (
                        <>
                            {errors.type === 'required' && `${label || 'Password'} is required.`}
                            {errors.type === 'minLength' && `${label || 'Password'} should be at least ${validation.minLength} characters long.`}
                            {errors.type === 'pattern' && `${label || 'Password'} format is invalid.`}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

PasswordInput.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    register: PropTypes.func.isRequired,
    errors: PropTypes.object,
    validation: PropTypes.object,
    backgroundImage: PropTypes.string,
    backgroundPosition: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
};

export default PasswordInput;

